import { useRef } from 'react'
import { useDialog, useModalOverlay } from 'react-aria'
import type { OverlayTriggerState } from 'react-stately'

interface ConfirmDialogProps {
	title: string
	message: string
	confirmLabel?: string
	cancelLabel?: string
	onConfirm: () => void
	state: OverlayTriggerState
}

export function ConfirmDialog({
	title,
	message,
	confirmLabel = 'Confirm',
	cancelLabel = 'Cancel',
	onConfirm,
	state,
}: ConfirmDialogProps) {
	const overlayRef = useRef<HTMLDivElement>(null)
	const dialogRef = useRef<HTMLDivElement>(null)

	const { modalProps, underlayProps } = useModalOverlay({ isDismissable: true }, state, overlayRef)

	const { dialogProps, titleProps } = useDialog({ role: 'alertdialog' }, dialogRef)

	if (!state.isOpen) return null

	return (
		<div className='fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4' {...underlayProps}>
			<div ref={overlayRef} {...modalProps} className='bg-white rounded-lg shadow-xl max-w-md w-full'>
				<div ref={dialogRef} {...dialogProps} className='p-6'>
					<h3 {...titleProps} className='text-lg font-semibold text-neutral-900 mb-2'>
						{title}
					</h3>
					<p className='text-sm text-neutral-600 mb-6'>{message}</p>
					<div className='flex justify-end gap-3'>
						<button
							type='button'
							onClick={state.close}
							className='px-4 py-2 border border-neutral-300 text-neutral-700 rounded-md hover:bg-neutral-50 text-sm font-medium transition-colors'>
							{cancelLabel}
						</button>
						<button
							type='button'
							onClick={() => {
								onConfirm()
								state.close()
							}}
							className='px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm font-medium transition-colors'>
							{confirmLabel}
						</button>
					</div>
				</div>
			</div>
		</div>
	)
}
